// Dugout's pitches are full-canvas 615×615 backgrounds, but on the smaller forms only part of that is
// the pitch actually in use. At zoom 1 those read as a small pitch floating in empty green — the
// RINUS-175 feedback asked to zoom in on the pitch in use. 11v11 and Futsal already fill the canvas.
//
// This is a VIEWPORT zoom, not an artwork change: object coordinates are untouched, so the round-trip
// with existing Dugout drawings (and formation placement, which assumes the pitch renders at scale 1)
// is unaffected.

// The dugout canvas is a fixed 615 square (see setField in app/page.tsx — dugout uses scaleFactor 1,
// so the pitch maps 1:1 onto canvas units).
const CANVAS_SIZE = 615;
// How much of the canvas the active pitch should span, leaving a margin so items can still be parked
// just outside the lines.
const FILL_RATIO = 0.88;
// Same ceiling the pinch handler enforces (components/Canvas/Canvas.tsx), so a programmatic zoom never
// lands outside the range the tool is already exercised at.
const MAX_ZOOM = 2;

/**
 * The pitch actually in use, per field variation, in canvas units — measured off the committed artwork.
 * `field-03` is the 6v6 pitch as corrected for RINUS-175: two 6v6 pitches (30 m each, 4 m apart) lay out
 * on one 8v8 pitch, so it is 42,5 × 30 m = 210 × 149 px, top edge on the touchline. Unlike `field-02` it
 * is NOT canvas-centred, which is why the zoom has to pan rather than just scale about the centre.
 */
const ACTIVE_FIELD: Record<
  string,
  { left: number; top: number; width: number; height: number }
> = {
  'field-02': { left: 202, top: 148, width: 210, height: 318 },
  'field-03': { left: 182, top: 148, width: 210, height: 149 },
};

export interface DugoutFieldView {
  /** Viewport zoom to apply. 1 for pitches that already fill the canvas. */
  zoom: number;
  /** Canvas-space point to centre in the viewport. */
  focusX: number;
  focusY: number;
}

/**
 * Zoom + focus point for a Dugout field. Returns zoom 1 focused on the canvas centre for any field that
 * already fills the canvas (11v11, Futsal) and for unknown variations, so the caller can apply the
 * result unconditionally.
 */
const dugoutFieldZoom = (variation: string): DugoutFieldView => {
  const centre = CANVAS_SIZE / 2;
  const rect = ACTIVE_FIELD[variation];
  if (!rect) return { zoom: 1, focusX: centre, focusY: centre };

  // Square canvas, so the longer side of the pitch is the binding constraint.
  const fit = (CANVAS_SIZE * FILL_RATIO) / Math.max(rect.width, rect.height);
  return {
    zoom: Math.min(Math.max(fit, 1), MAX_ZOOM),
    focusX: rect.left + rect.width / 2,
    focusY: rect.top + rect.height / 2,
  };
};

export default dugoutFieldZoom;
